import { useEffect, useMemo } from "react";
import { throttle } from "lodash";
import { CANVAS_SIZE_ID } from "../Canvas/constants";
import { IFRAME_ID } from "../Frames/constants";
import { useBuilderStore } from "../Hooks/useBuilderStore";
import { useIFrameDocument } from "../Hooks/useIFrameDocument";

export const useResizeListeners = ({
  updatePosition,
}: {
  updatePosition: () => void;
}) => {
  const { selectedBlockPath, selectedBlock } = useBuilderStore(state => {
    const value = state.getSelectedBlock();
    return {
      selectedBlockPath: state.selectedBlockPath,
      selectedBlock: value?.block,
    };
  });
  const iFrameDocument = useIFrameDocument(IFRAME_ID, selectedBlockPath ?? "");

  const resizeObserver = useMemo(
    () =>
      new ResizeObserver(entries => {
        for (const entry of entries) {
          if (entry.contentRect) {
            updatePosition();
          }
        }
      }),
    [updatePosition],
  );

  useEffect(() => {
    if (!iFrameDocument) {
      return;
    }

    const throttledUpdatePosition = throttle(updatePosition, 100);
    iFrameDocument.addEventListener("scroll", throttledUpdatePosition);
    window.addEventListener("resize", throttledUpdatePosition);
    window.addEventListener("scroll", throttledUpdatePosition);

    const iframe = document.getElementById(IFRAME_ID);
    if (iframe) {
      resizeObserver.observe(iframe);
    }

    const canvasSizeElement = document.getElementById(CANVAS_SIZE_ID);
    if (canvasSizeElement) {
      resizeObserver.observe(canvasSizeElement);
    }

    return () => {
      throttledUpdatePosition.cancel();
      iFrameDocument.removeEventListener("scroll", throttledUpdatePosition);
      window.removeEventListener("resize", throttledUpdatePosition);
      window.removeEventListener("scroll", throttledUpdatePosition);
      if (canvasSizeElement) resizeObserver.unobserve(canvasSizeElement);
      if (iframe) resizeObserver.unobserve(iframe);
    };
  }, [iFrameDocument, selectedBlock, updatePosition, resizeObserver]);

  useEffect(() => {
    const { id } = selectedBlock || {};
    if (!id || !iFrameDocument) {
      return;
    }

    const blockElement = iFrameDocument.getElementById(id);
    if (!blockElement) {
      return;
    }

    resizeObserver.observe(blockElement);

    const images = blockElement.querySelectorAll("img");
    images.forEach(img => {
      if (img.complete) {
        updatePosition();
      } else {
        img.addEventListener("load", updatePosition);
      }
    });

    return () => {
      resizeObserver.unobserve(blockElement);
      images.forEach(img => {
        img.removeEventListener("load", updatePosition);
      });
    };
  }, [iFrameDocument, selectedBlock, resizeObserver, updatePosition]);
};
